import React, { FC, useContext, useEffect, useState } from "react";
import { IDatabaseReference, api } from "../../logic/api";
import { IUser, SessionContext } from "../../utility/useSession";
import { Button, useMediaQuery, useTheme } from '@mui/material';
import AnimateFrom from "../../components/animations/AnimateFrom";
import { Selectable } from "../../components/lists/Selectable/Selectable";
import styles from './styles';
import { useTranslation } from "react-i18next";
import { LoginTitle } from "./LoginPageV2";
import { FaSpinner } from "react-icons/fa";
import { SnackAlert } from "../../components/notifications/snack/SnackAlert";

interface TenantSelectorProps {
  onReset: () => void;
}

export const TenantSelector: FC<TenantSelectorProps> = ({
  onReset
}) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const { t } = useTranslation();

  const [database, setDatabase] = useState<IDatabaseReference | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const availableTenants = api.hooks.auth.availableTenants();
  const selectTenant = api.hooks.auth.select();

  useEffect(() => {
    availableTenants.send(
      session.current.context,
      {},
      (response) => {
        if (!response) {
          return;
        }

        if (response.tenants.length === 1) {
          selectDatabase(response.tenants[0]);
        }
      }
    )
  }, []);

  const user = session.current.credential as IUser;

  const selectDatabase = (database: IDatabaseReference) => {
    setIsBusy(true);

    selectTenant.send(
      session.current.context,
      {
        identity: database.identity,
        homeLocationCode: "",
      },
      (response) => {
        if (!response) {
          setIsBusy(false);
          return;
        }

        session.login(response.token, {
          firstName: user.firstName,
          lastName: user.lastName,
          database: database.name,
          identity: database.identity,
          clientCode: database.clientCode,
          twoFactorEnabled: user.twoFactorEnabled
        });

        setIsBusy(false);
      }
    );
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!database) {
      return;
    }

    selectDatabase(database);
  }

  const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
  const fieldStyle = isLargeDevice == true ? styles.field : { ...styles.field, ...styles.fieldSm };

  return <div>
    <LoginTitle title={t("login.site.title", { name: user.firstName })} message={t("login.site.message")} />

    <form id="tenant-selector" onSubmit={handleSubmit} onReset={onReset}>
      <AnimateFrom style={{ opacity: 0, x: 100 }}>
        <Selectable<IDatabaseReference>
          style={fieldStyle}
          items={availableTenants.data?.tenants || []}
          onChange={(value) => {
            setDatabase(value);
          }}
          initialValue={database}
          onRenderItem={(item) => {
            return <div data-testid={`tenant-option-${item.name}`}>{item.name}</div>
          }}
          data-testid="tenant-selector"
        />
      </AnimateFrom>

      <div style={{ marginTop: 20 }}></div>

      <div style={styles.row}>
        <div style={isLargeDevice ? styles.alignRight : styles.alignRightSm}>
          <AnimateFrom inline={true} delay={1} style={{ opacity: 0, x: 100 }}>
            <Button type="submit" disabled={!database || isBusy} style={styles.buttons.primary} variant="contained" color="secondary" data-testid="tenant-confirm-button">
              {t("general.confirm")}
              {isBusy && (<FaSpinner className="spin" style={{ marginLeft: 5 }} />)}
            </Button>
          </AnimateFrom>

          <AnimateFrom inline={true} delay={1.5} style={{ opacity: 0, x: 100 }}>
            <Button type="reset" disabled={isBusy} style={styles.buttons.secondary} variant="outlined" color="secondary">{t("general.cancel")}</Button>
          </AnimateFrom>
        </div>
      </div>
    </form>

    <SnackAlert id="loginError" message={selectTenant.err} ></SnackAlert >
  </div>
}
